/**
 * supabase.js - Supabase client configuration and connection management
 *
 * Handles Supabase database client setup for TimeGenie:
 * - Environment variable validation and logging
 * - Custom fetch function with authentication and request logging
 * - Client configuration with auth settings and custom storage
 * - iOS-specific secure storage using Keychain
 * - Connection testing and error handling
 * - Consistent error handling utilities
 */

import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';

import logger from '../utils/logger';

// Load Supabase configuration from environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const NODE_ENV = process.env.NODE_ENV;
const IS_PRODUCTION = NODE_ENV === 'production';
const IS_TEST = NODE_ENV === 'test';

// Log environment variable status (without exposing sensitive data)
logger.info('Supabase URL:', SUPABASE_URL ? '✅ Present' : '❌ Missing');
logger.info('Supabase Anon Key:', SUPABASE_ANON_KEY ? '✅ Present' : '❌ Missing');

// Validate required environment variables
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  logger.error('❌ Missing required Supabase environment variables. Please check your .env file.');
  logger.info('Make sure your .env file contains:');
  logger.info('REACT_APP_SUPABASE_URL=your_project_url');
  logger.info('REACT_APP_SUPABASE_ANON_KEY=your_anon_key');

  if (!IS_TEST) {
    // Fail fast outside of tests so misconfiguration cannot silently leak traffic to an unintended host.
    throw new Error('Missing Supabase configuration.');
  }
}

// Removed customFetch function that was causing query timeouts
// Using default fetch implementation for better reliability

// Detect if running on native iOS/Android
const IS_NATIVE = (() => {
  try {
    return Capacitor.isNativePlatform();
  } catch (_) {
    return false;
  }
})();

logger.info('Supabase: Platform detection -', IS_NATIVE ? 'Native (iOS/Android)' : 'Web');

/**
 * Custom storage adapter for iOS that uses Capacitor Secure Storage (Keychain)
 * On web, falls back to localStorage
 *
 * Supabase v2 supports async storage adapters - getItem/setItem/removeItem can return Promises
 */
const createSecureStorage = () => {
  if (!IS_NATIVE) {
    // Web: use default localStorage
    return undefined; // Let Supabase use its default
  }

  // Native: Use secure storage (Keychain on iOS)
  // We dynamically import to avoid bundling issues on web
  let SecureStoragePlugin = null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('capacitor-secure-storage-plugin');
    SecureStoragePlugin = mod.SecureStoragePlugin;
  } catch (e) {
    logger.warn('Supabase: SecureStoragePlugin not available, falling back to localStorage');
    return undefined;
  }

  if (!SecureStoragePlugin) {
    return undefined;
  }

  logger.info('Supabase: Using Keychain-backed secure storage for auth on iOS');

  return {
    async getItem(/** @type {string} */ key) {
      try {
        const result = await SecureStoragePlugin.get({ key });
        return result.value;
      } catch (error) {
        // SecureStoragePlugin throws if key doesn't exist - that's normal
        const err = /** @type {Error & { code?: string }} */ (error);
        if (err.message?.includes('not found') || err.code === 'ERR_NO_VALUE') {
          return null;
        }
        // Log unexpected errors but try localStorage fallback
        logger.warn('Supabase: SecureStorage.get failed for key:', key, error);
        try {
          return localStorage.getItem(key);
        } catch (_) {
          return null;
        }
      }
    },
    async setItem(/** @type {string} */ key, /** @type {string} */ value) {
      try {
        await SecureStoragePlugin.set({ key, value });
      } catch (error) {
        logger.warn('Supabase: SecureStorage.set failed for key:', key, error);
        // Fallback to localStorage
        try {
          localStorage.setItem(key, value);
        } catch (_) {
          // Ignore
        }
      }
    },
    async removeItem(/** @type {string} */ key) {
      try {
        await SecureStoragePlugin.remove({ key });
      } catch (error) {
        logger.warn('Supabase: SecureStorage.remove failed for key:', key, error);
        // Fallback to localStorage
        try {
          localStorage.removeItem(key);
        } catch (_) {
          // Ignore
        }
      }
    },
  };
};

// Create and configure the Supabase client with simplified settings
// In development on native (iOS simulator), proxy Supabase requests through
// the CRA dev server to avoid WKWebView "Load failed" on cross-origin HTTPS.
// See src/setupProxy.js for the server-side proxy configuration.
const useDevProxy = !IS_PRODUCTION && !IS_TEST && typeof window !== 'undefined' && !!SUPABASE_URL;
if (useDevProxy) {
  logger.info(
    'Supabase: Using dev server proxy →',
    window.location?.origin,
    '(setupProxy.js forwards to Supabase)',
  );
}
const supabaseUrl = useDevProxy
  ? window.location.origin
  : SUPABASE_URL || (IS_TEST ? 'http://localhost:54321' : null);
const supabaseAnonKey = SUPABASE_ANON_KEY || (IS_TEST ? 'test-anon-key' : null);

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase client cannot be initialized without URL and anon key.');
}

// Get secure storage adapter (returns undefined on web, Keychain adapter on iOS)
const secureStorage = createSecureStorage();

const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true, // Automatically refresh expired tokens
    persistSession: true, // Keep session across browser restarts
    detectSessionInUrl: true, // Handle auth tokens in URL (for invite links)
    // Use secure storage on iOS, default localStorage on web
    ...(secureStorage && { storage: secureStorage }),
  },
});

// Client-side health checks can leak data if RLS is misconfigured. Prefer server-side
// monitoring instead of running browser queries against privileged tables.

export { supabase };

/**
 * handleSupabaseError - Consistent error handling for Supabase operations
 *
 * @param {Error} error - The error object from Supabase
 * @throws {Error} - Re-throws the error after logging
 *
 * Provides centralized error logging and handling for all Supabase operations
 * Can be extended to include error reporting, user notifications, etc.
 */
const handleSupabaseError = (error) => {
  logger.error('Supabase Error:', error);
  throw error;
};
